import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Plus, Trash, Handshake, Heart, UsersThree } from "@phosphor-icons/react";
import { Section, Pill, EmptyState } from "./_shared";
import logger from "@/lib/logger";

const LMH = ["L", "M", "H"];

const lmhTone = (v) => v === "H" ? "red" : v === "M" ? "orange" : "teal";

export default function Stakeholders({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", role: "", influence_level: "M", interest_level: "M" });
  const [creating, setCreating] = useState(false);
  const [engagingId, setEngagingId] = useState(null);
  const [trustId, setTrustId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/stakeholders`);
      setItems(data);
    } catch (err) {
      logger.warn("Stakeholders load failed:", err?.response?.data?.detail || err?.message);
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/stakeholders`, form);
      setItems((p) => [data, ...p]);
      setForm({ name: "", role: "", influence_level: "M", interest_level: "M" });
    } catch { toast.error("Failed to add"); }
    finally { setCreating(false); }
  };

  const engage = async (id) => {
    setEngagingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/stakeholders/${id}/engagement`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Engagement plan generated");
    } catch { toast.error("Failed"); }
    finally { setEngagingId(null); }
  };

  const trust = async (id) => {
    setTrustId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/stakeholders/${id}/trust-gap`);
      setItems((p) => p.map(x => x.id === id ? { ...x, ...data } : x));
      toast.success("Trust gap analysed");
    } catch { toast.error("Failed"); }
    finally { setTrustId(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/projects/${projectId}/stakeholders/${id}`); setItems(p => p.filter(x => x.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-8" data-testid="stakeholders-section">
      <Section eyebrow="ML 4.1 · Engagement" title={<><UsersThree className="inline -mt-1" size={20} weight="duotone" /> Stakeholders</>}>
        <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto_auto_auto] gap-3 mb-4">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Stakeholder name"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="stakeholder-name-input" />
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            placeholder="Role (e.g. CFO, Vendor PM)"
            className="h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="stakeholder-role-input" />
          <select value={form.influence_level} onChange={(e) => setForm({ ...form, influence_level: e.target.value })}
            className="h-12 px-3 border border-zinc-300 text-sm bg-white"
            data-testid="stakeholder-influence-select">
            {LMH.map(l => <option key={l} value={l}>Inf: {l}</option>)}
          </select>
          <select value={form.interest_level} onChange={(e) => setForm({ ...form, interest_level: e.target.value })}
            className="h-12 px-3 border border-zinc-300 text-sm bg-white"
            data-testid="stakeholder-interest-select">
            {LMH.map(l => <option key={l} value={l}>Int: {l}</option>)}
          </select>
          <button type="submit" disabled={creating}
            className="h-12 px-5 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="add-stakeholder-button">
            <Plus size={16} weight="bold" /> {creating ? "Adding..." : "Add"}
          </button>
        </form>

        {loading ? <div className="text-zinc-500 text-sm">Loading…</div> :
          items.length === 0 ? <EmptyState title="No stakeholders captured" hint="Add stakeholders to build engagement plans and trust strategies." testid="empty-stakeholders" /> :
          <div className="overflow-x-auto border border-zinc-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Name · Role</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Influence</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Interest</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Engagement strategy</th>
                  <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Trust gap · action</th>
                  <th className="text-right px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(s => (
                  <tr key={s.id} className="border-b border-zinc-100 align-top" data-testid={`stakeholder-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display font-bold">{s.name}</div>
                      {s.role && <div className="text-xs text-zinc-500">{s.role}</div>}
                    </td>
                    <td className="px-4 py-3"><Pill tone={lmhTone(s.influence_level)}>{s.influence_level}</Pill></td>
                    <td className="px-4 py-3"><Pill tone={lmhTone(s.interest_level)}>{s.interest_level}</Pill></td>
                    <td className="px-4 py-3 max-w-xs">
                      {s.engagement_strategy ? (
                        <div>
                          <p className="text-sm text-zinc-800">{s.engagement_strategy}</p>
                          {s.communication_method && <p className="text-xs text-zinc-500 mt-1">via {s.communication_method}</p>}
                        </div>
                      ) : <span className="text-xs text-zinc-400">—</span>}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {s.trust_gap ? (
                        <div>
                          <p className="text-xs font-semibold text-zinc-700">Gap: {s.trust_gap}</p>
                          {s.proposed_action && <p className="text-xs text-zinc-700 mt-1">Action: {s.proposed_action}</p>}
                          {s.expected_outcome && <p className="text-xs text-zinc-500 mt-1 italic">{s.expected_outcome}</p>}
                        </div>
                      ) : <span className="text-xs text-zinc-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-end">
                        <button onClick={() => engage(s.id)} disabled={engagingId === s.id}
                          className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#7C3AED] text-white text-[11px] font-bold hover:bg-purple-700 disabled:opacity-50"
                          data-testid={`engage-stakeholder-${s.id}`}>
                          <Handshake size={11} weight="fill" />
                          {engagingId === s.id ? "Working…" : "Engagement"}
                        </button>
                        <button onClick={() => trust(s.id)} disabled={trustId === s.id}
                          className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#FF3B30] text-white text-[11px] font-bold hover:bg-red-700 disabled:opacity-50"
                          data-testid={`trust-stakeholder-${s.id}`}>
                          <Heart size={11} weight="fill" />
                          {trustId === s.id ? "Working…" : "Trust gap"}
                        </button>
                        <button onClick={() => remove(s.id)}
                          className="inline-flex items-center gap-1.5 h-8 px-3 border border-zinc-300 text-zinc-600 text-[11px] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                          data-testid={`delete-stakeholder-${s.id}`}>
                          <Trash size={11} weight="bold" /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </Section>
    </div>
  );
}
